// Dev-only tool, mounted from index.js only when DEV_PREVIEW is true (i.e.
// window.skyrimPlatform doesn't exist - we're running under `yarn dev` in a
// plain browser, not inside the game's CEF). Lets you fire each panel's
// "show" CustomEvent with reasonable mock data from a floating button list,
// instead of dispatching events by hand in the console one at a time.

interface PanelEntry {
  label: string;
  show: () => void;
  hide?: () => void;
}

const dispatch = (name: string, detail?: unknown) => {
  window.dispatchEvent(new CustomEvent(name, detail === undefined ? {} : { detail }));
};

// Mock server state for the contracts panel (contractsSystem.ts's contractsState packet)
const contractsMock = (filled: boolean) => {
  const now = Date.now();
  const H = 3600_000;
  const contract = (id: number, over: Record<string, unknown>) => ({
    id,
    category: 'livraison',
    categoryLabel: 'Livraison',
    title: 'Colis pour Blancherive',
    description: 'Un colis scellé à remettre au forgeron Adrianne.',
    reward: 250,
    creatorName: 'Maren Voss',
    workerName: null,
    role: 'none',
    state: 'open',
    stateLabel: 'Ouvert',
    createdAt: now - 3 * H,
    deadlineAt: now + 52 * H,
    deliveredAt: null,
    reviewAt: null,
    events: [],
    ...over,
  });
  const mineEvents = (...steps: Array<[string, string, number]>) =>
    steps.map(([state, by, ago]) => ({ at: now - ago * H, state, by }));
  return {
    now,
    money: 1840,
    categories: [
      { id: 'mercenaire', label: 'Mercenaire' },
      { id: 'caravane', label: 'Caravane' },
      { id: 'livraison', label: 'Livraison' },
      { id: 'garde_du_corps', label: 'Garde du corps' },
      { id: 'chasse', label: 'Chasse' },
      { id: 'fabrication', label: 'Fabrication' },
      { id: 'escorte', label: 'Escorte' },
      { id: 'commerce', label: 'Commerce' },
      { id: 'exploration', label: 'Exploration' },
      { id: 'divers', label: 'Divers' },
    ],
    deadlineDays: [1, 3, 7, 14],
    limits: { minReward: 10, maxReward: 100000, maxTitle: 60, maxDescription: 300, maxOpen: 5, maxAccepted: 3 },
    available: filled
      ? [
        contract(11, {}),
        contract(12, { category: 'chasse', categoryLabel: 'Chasse', title: 'Trois peaux de loup des neiges', description: '', reward: 120, creatorName: 'Hjalti', deadlineAt: now + 20 * H }),
        contract(13, { category: 'escorte', categoryLabel: 'Escorte', title: "Escorte jusqu'à Vendeaume", description: 'Départ au lever du jour depuis Fort-Dawnguard, retour non nécessaire.', reward: 900, creatorName: 'Sigrid la Grise', deadlineAt: now + 6 * 24 * H }),
      ]
      : [],
    mine: filled
      ? [
        contract(21, { role: 'creator', title: 'Réparer la palissade', category: 'fabrication', categoryLabel: 'Fabrication', reward: 400, state: 'delivered', stateLabel: 'Livré', workerName: 'Eydis', creatorName: 'Toi', deliveredAt: now - 5 * H, reviewAt: now + 43 * H, events: mineEvents(['open', 'Toi', 30], ['accepted', 'Eydis', 20], ['delivered', 'Eydis', 5]) }),
        contract(22, { role: 'worker', title: 'Message pour le jarl', reward: 150, state: 'accepted', stateLabel: 'En cours', creatorName: 'Maren Voss', workerName: 'Toi', events: mineEvents(['open', 'Maren Voss', 8], ['accepted', 'Toi', 2]) }),
        contract(23, { role: 'creator', title: 'Cueillir de la fleur de montagne', category: 'divers', categoryLabel: 'Divers', reward: 60, creatorName: 'Toi', events: mineEvents(['open', 'Toi', 1]) }),
        contract(24, { role: 'creator', title: 'Garde de la caravane', category: 'caravane', categoryLabel: 'Caravane', reward: 1500, state: 'disputed', stateLabel: 'Contesté', workerName: 'Brynjolf', creatorName: 'Toi', deliveredAt: now - 30 * H, events: mineEvents(['open', 'Toi', 90], ['accepted', 'Brynjolf', 80], ['delivered', 'Brynjolf', 30], ['disputed', 'Toi', 29]) }),
        contract(25, { role: 'worker', title: 'Prospection au nord', category: 'exploration', categoryLabel: 'Exploration', reward: 300, state: 'settled', stateLabel: 'Réglé', workerName: 'Toi', events: mineEvents(['open', 'Aela', 120], ['accepted', 'Toi', 100], ['delivered', 'Toi', 60], ['settled', 'système', 12]) }),
        contract(26, { role: 'creator', title: 'Chasse au cerf', category: 'chasse', categoryLabel: 'Chasse', reward: 90, state: 'expired', stateLabel: 'Expiré', creatorName: 'Toi', events: mineEvents(['open', 'Toi', 100], ['expired', 'système', 28]) }),
      ]
      : [],
  };
};

const depotMock = (jarl: boolean) => ({
  holdId: 'blancherive',
  holdName: 'Blancherive',
  fee: 5,
  feeMax: 50,
  canSetFee: jarl,
  usersCount: jarl ? 14 : null,
  gold: 320,
  bag: [
    { baseId: 1, name: 'Lingot de fer', type: 'MISC', count: 12 },
    { baseId: 2, name: 'Peau de loup', type: 'MISC', count: 4 },
    { baseId: 3, name: 'Fleur de montagne bleue', type: 'INGR', count: 23 },
    { baseId: 4, name: 'Épée en acier', type: 'WEAP', count: 1 },
    { baseId: 5, name: 'Potion de soins mineurs', type: 'ALCH', count: 6 },
  ],
  depot: [
    { baseId: 6, name: 'Minerai de fer', type: 'MISC', count: 140 },
    { baseId: 7, name: 'Lingot d\'argent', type: 'MISC', count: 32 },
    { baseId: 8, name: 'Cuirasse en cuir', type: 'ARMO', count: 2 },
  ],
  usage: { types: 3, units: 174 },
  limits: { maxTypes: 60, maxUnits: 2000 },
  catalogReady: true,
});

const panels: PanelEntry[] = [
  {
    label: 'CitySelection',
    show: () => dispatch('skymp5-client:showCitySelection', {
      cities: [
        { id: 'whiterun', name: 'Blancherive', hold: 'Blancherive', description: 'Ville de test.' },
        { id: 'solitude', name: 'Solitude', hold: 'Haafingar', description: 'Ville de test.' },
      ],
    }),
  },
  {
    label: 'TargetMenu',
    show: () => dispatch('skymp5-client:showTargetMenu', {
      options: [
        { id: '1', label: 'Se présenter', group: 'Social' },
        { id: '2', label: 'Se présenter à tous', group: 'Social' },
        { id: '3', label: 'Échanger', group: 'Social' },
        { id: '4', label: 'Demander les papiers', group: 'Papiers' },
        { id: '5', label: 'Accorder : Haafingar', group: 'Papiers' },
        { id: '6', label: 'Recruter', group: 'Faction' },
        { id: '7', label: 'Renvoyer', group: 'Faction', danger: true },
        { id: '8', label: 'Mettre une amende', group: 'Autorité' },
        { id: '9', label: 'Attacher', group: 'Contrainte', danger: true },
        { id: '10', label: 'Porter', group: 'Contrainte' },
      ],
    }),
    hide: () => dispatch('skymp5-client:hideTargetMenu'),
  },
  {
    label: 'EmoteWheel',
    show: () => dispatch('skymp5-client:showEmoteWheel'),
    hide: () => dispatch('skymp5-client:hideEmoteWheel'),
  },
  {
    label: 'ProfessionsMenu',
    show: () => dispatch('skymp5-client:showProfessionsMenu', {
      ranks: [{ id: 0, name: 'Apprenti', xpRequired: 0 }, { id: 1, name: 'Confirmé', xpRequired: 100 }],
      professions: [{ id: 'test-job', name: 'Métier de test', category: 'job', activities: [] }],
      ownProfessions: { jobs: [], specialization: null, hasRerolled: false },
    }),
    hide: () => dispatch('skymp5-client:hideProfessionsMenu'),
  },
  {
    label: 'CraftingSession',
    show: () => dispatch('skymp5-client:showCraftingSession', {
      sessionId: 'test', activity: 'smith', recipeName: 'Objet de test',
      ingredients: [{ itemFormId: '1', name: 'Lingot de fer', count: 2 }],
      difficulty: 1, durationMs: 10000,
      minigame: {
        idealMin: 40, idealMax: 60, hits: [{ windowStartMs: 1000, windowEndMs: 2000 }],
        tempPhaseMaxMs: 3000, hammerPhaseMaxMs: 5000, quenchPhaseMaxMs: 2000,
        quenchWindowStartMs: 500, quenchWindowEndMs: 1000,
      },
    }),
    hide: () => dispatch('skymp5-client:hideCraftingSession'),
  },
  {
    label: 'FishingMinigame',
    show: () => dispatch('skymp5-client:showFishingMinigame', { step: 'cast', requestId: 1 }),
    hide: () => dispatch('skymp5-client:hideFishingMinigame'),
  },
  {
    label: 'CraftMenu',
    show: () => dispatch('skymp5-client:showCraftMenu', {
      activity: 'smith', professionId: 'test-job', currentRankId: 0, recipes: [], ranks: [],
    }),
    hide: () => dispatch('skymp5-client:hideCraftMenu'),
  },
  {
    label: 'StationSpawner',
    show: () => dispatch('skymp5-client:showStationSpawner', { stations: [] }),
    hide: () => dispatch('skymp5-client:hideStationSpawner'),
  },
  {
    label: 'Toast',
    show: () => dispatch('skymp5-client:toast', { key: 'test', text: 'Message de test' }),
  },
  {
    label: 'AdminPanel (propriétaire)',
    show: () => showAdminPanel(ALL_ADMIN_CAPS.slice()),
    hide: () => dispatch('skymp5-client:hideAdminMenu'),
  },
  {
    label: 'AdminPanel (5000 joueurs)',
    show: () => showAdminPanel(ALL_ADMIN_CAPS.slice(), true),
    hide: () => dispatch('skymp5-client:hideAdminMenu'),
  },
  {
    label: 'AdminPanel (modérateur)',
    show: () => showAdminPanel(['players', 'kick', 'ban', 'reports', 'broadcast']),
    hide: () => dispatch('skymp5-client:hideAdminMenu'),
  },
  {
    label: 'Vitals',
    show: () => dispatch('skymp5-client:vitalsState', { drunk: 3, hunger: 3 }),
  },
  {
    label: 'DownedScreen',
    show: () => dispatch('skymp5-client:showDownedScreen', { remainingMs: 30000 }),
    hide: () => dispatch('skymp5-client:hideDownedScreen'),
  },
  {
    label: 'EscMenu',
    show: () => dispatch('skymp5-client:showEscMenu'),
    hide: () => dispatch('skymp5-client:hideEscMenu'),
  },
  {
    label: 'Trade',
    show: () => dispatch('skymp5-client:showTradeMenu', { yourItems: [], theirItems: [], theirName: 'Test' }),
    hide: () => dispatch('skymp5-client:hideTradeMenu'),
  },
  {
    label: 'CaptureConsent',
    show: () => dispatch('skymp5-client:showCaptureConsent', { text: 'Voulez-vous capturer cette personne ?' }),
    hide: () => dispatch('skymp5-client:hideCaptureConsent'),
  },
  {
    label: 'DoorLockConfig',
    show: () => dispatch('skymp5-client:showDoorLockConfig', {
      refrId: 12345,
      factions: [{ id: 'thalmor', name: 'Thalmor', ranks: [{ id: 0, name: 'Recrue' }] }],
      identities: [{ actorId: 101, name: 'Elenwen', online: true }],
      current: null,
    }),
  },
  {
    label: 'DungeonLootConfig',
    show: () => dispatch('skymp5-client:showDungeonLootConfig', { dungeonId: 'test', refrId: 1 }),
  },
  {
    label: 'DungeonManager',
    show: () => dispatch('skymp5-client:showDungeonManager', { dungeons: [] }),
  },
  {
    label: 'DungeonSearchProgress',
    show: () => dispatch('skymp5-client:dungeonSearch', { visible: true, progress: 0.4 }),
  },
  {
    label: 'WildlifeZoneManager',
    show: () => dispatch('skymp5-client:showWildlifeZoneManager', { zones: [] }),
  },
  {
    label: 'ZonePreviewOverlay',
    show: () => dispatch('skymp5-client:zonePreviewUpdate', { visible: true }),
  },
  {
    label: 'ZoneTypePicker',
    show: () => dispatch('skymp5-client:showZoneTypePicker', { zoneId: 'test', options: [] }),
  },
  {
    label: 'HouseAdminConfig',
    show: () => dispatch('skymp5-client:showHouseAdminConfig', { refrId: 1, currentPrice: 500 }),
  },
  {
    label: 'HouseBuyPrompt',
    show: () => dispatch('skymp5-client:promptHouseBuy', { refrId: 1, price: 500 }),
  },
  {
    label: 'HousingManage',
    show: () => dispatch('skymp5-client:housingManageState', {
      houses: [{
        refrId: 1, displayName: 'Maison de test', members: [], maxMembers: 5,
        isLocked: false, isOwner: true, chests: [], maxChests: 3,
      }],
    }),
    hide: () => dispatch('skymp5-client:hideHousingManage'),
  },
  {
    label: 'HorseStable',
    show: () => dispatch('skymp5-client:showHorseStable', { breeds: [], current: null }),
  },
  {
    label: 'DoorCodePrompt',
    show: () => dispatch('skymp5-client:promptDoorCode', { refrId: 1 }),
  },
  {
    label: 'DoorLocksList',
    show: () => dispatch('skymp5-client:showDoorLocksList', { locks: [] }),
  },
  {
    label: 'AfkChallenge',
    show: () => dispatch('skymp5-client:showAfkChallenge', { word: 'test' }),
    hide: () => dispatch('skymp5-client:hideAfkChallenge'),
  },
  {
    label: 'FinePrompt',
    show: () => dispatch('skymp5-client:showFinePrompt', { target: 1, targetName: 'Test' }),
  },
  {
    label: 'Mailbox',
    show: () => dispatch('skymp5-client:showMailbox', { messages: [], pendingCount: 0 }),
    hide: () => dispatch('skymp5-client:hideMailbox'),
  },
  {
    label: 'Depot',
    show: () => dispatch('skymp5-client:showDepot', { state: depotMock(false) }),
    hide: () => dispatch('skymp5-client:hideDepot'),
  },
  {
    label: 'Depot (Jarl)',
    show: () => dispatch('skymp5-client:showDepot', { state: depotMock(true) }),
    hide: () => dispatch('skymp5-client:hideDepot'),
  },
  {
    label: 'Depot (vide)',
    show: () => dispatch('skymp5-client:showDepot', {
      state: {
        ...depotMock(false), fee: 0, bag: [], depot: [], usage: { types: 0, units: 0 },
      },
    }),
    hide: () => dispatch('skymp5-client:hideDepot'),
  },
  {
    label: 'Depot (chargement)',
    show: () => dispatch('skymp5-client:showDepot', {}),
    hide: () => dispatch('skymp5-client:hideDepot'),
  },
  {
    label: 'Depot (erreur)',
    show: () => {
      dispatch('skymp5-client:showDepot', {});
      dispatch('skymp5-client:depotError', { text: 'Tu as quitté le fief : l\'entrepôt n\'est plus accessible.' });
    },
    hide: () => dispatch('skymp5-client:hideDepot'),
  },
  {
    label: 'Contrats',
    show: () => {
      dispatch('skymp5-client:showContracts', {});
      dispatch('skymp5-client:contractsState', contractsMock(true));
    },
    hide: () => dispatch('skymp5-client:hideContracts'),
  },
  {
    label: 'Contrats (vide)',
    show: () => {
      dispatch('skymp5-client:showContracts', {});
      dispatch('skymp5-client:contractsState', contractsMock(false));
    },
    hide: () => dispatch('skymp5-client:hideContracts'),
  },
  {
    label: 'Contrats (sans reponse)',
    show: () => dispatch('skymp5-client:showContracts', {}),
    hide: () => dispatch('skymp5-client:hideContracts'),
  },
  {
    label: 'PublicWork (liste)',
    show: () => dispatch('skymp5-client:showPublicWork', {
      state: {
        serverNow: Date.now(),
        cooldownUntil: Date.now() + 4 * 60_000 + 20_000,
        dailyPaid: 340,
        dailyCap: 1500,
        active: null,
        offers: [
          { templateId: 'hay_delivery', label: 'Livraison de foin', description: '', fromName: 'Blancherive', toName: 'Épervine', reward: 31, durationMs: 35 * 60_000 },
          { templateId: 'firewood_delivery', label: 'Livraison de bois de chauffe', description: '', fromName: 'Vendeaume', toName: 'Fortdhiver', reward: 34, durationMs: 35 * 60_000 },
          { templateId: 'courier_run', label: 'Course de courrier', description: '', fromName: 'Solitude', toName: 'Morthal', reward: 31, durationMs: 30 * 60_000 },
          { templateId: 'goods_haul', label: 'Portage de marchandises', description: '', fromName: 'Blancherive', toName: 'Aubétoile', reward: 58, durationMs: 40 * 60_000 },
          { templateId: 'stable_supply', label: "Ravitaillement d'écurie", description: '', fromName: 'Morthal', toName: 'Aubétoile', reward: 37, durationMs: 35 * 60_000 },
        ],
      },
    }),
    hide: () => dispatch('skymp5-client:hidePublicWork'),
  },
  {
    label: 'PublicWork (course active)',
    show: () => dispatch('skymp5-client:showPublicWork', {
      state: {
        serverNow: Date.now(),
        cooldownUntil: 0,
        dailyPaid: 340,
        dailyCap: 1500,
        active: {
          templateId: 'goods_haul',
          label: 'Portage de marchandises',
          state: 'in_progress',
          stepLabel: 'Livrer le colis',
          fromName: 'Blancherive',
          toName: 'Aubétoile',
          reward: 58,
          deadline: Date.now() + 22 * 60_000 + 15_000,
        },
        offers: [],
      },
    }),
    hide: () => dispatch('skymp5-client:hidePublicWork'),
  },
  {
    label: 'PapersMenu',
    show: () => dispatch('skymp5-client:showPapersMenu', { papers: [], factions: [], canForge: false }),
    hide: () => dispatch('skymp5-client:hidePapersMenu'),
  },
  {
    label: 'PapersShow',
    show: () => dispatch('skymp5-client:showPapersConsent', { fromName: 'Quelqu\'un', label: 'ses papiers' }),
    hide: () => dispatch('skymp5-client:hidePapersConsent'),
  },
  {
    label: 'ItemGiver',
    show: () => dispatch('skymp5-client:showItemGiver', { catalog: [], catalogReady: true }),
    hide: () => dispatch('skymp5-client:hideItemGiver'),
  },
  {
    label: 'AdminBroadcast',
    show: () => dispatch('skymp5-client:adminBroadcast', {
      text: 'Le serveur redémarre dans 5 minutes, merci de vous mettre en sécurité.',
      durationMs: 10000,
    }),
  },
  {
    label: 'FineResponse',
    show: () => dispatch('skymp5-client:showFineResponse', {
      fineId: 'test', issuerName: 'Garde Test', holdId: 'whiterun', holdName: 'Blancherive', amount: 100,
    }),
  },
];


// ---------------------------------------------------------------------------
// Admin panel mock: two sets (owner, limited moderator) and a fake server that
// answers what the panel sends (the panel's send() raises 'skymp5-front:outgoing'
// outside the game).
// ---------------------------------------------------------------------------

const ALL_ADMIN_CAPS = [
  'players', 'teleport', 'modes', 'items', 'kick', 'ban', 'broadcast', 'world', 'restart', 'weather',
  'reports', 'housing', 'debug', 'console', 'staff',
];

const ADMIN_CAP_INFO: Record<string, { label: string; description: string }> = {
  players: { label: 'Joueurs', description: 'Soigner, geler, ressusciter, invoquer, rangs, métiers, emplacements de personnage, apparence.' },
  teleport: { label: 'Téléportation', description: 'Se téléporter aux lieux enregistrés.' },
  modes: { label: 'Modes', description: 'Invisibilité, mode dieu, traversée des murs et autres modes admin.' },
  items: { label: 'Objets et or', description: "Donner ou poser des objets, créer de l'or, vider un inventaire." },
  kick: { label: 'Expulsions', description: 'Expulser un joueur du serveur.' },
  ban: { label: 'Bannissements', description: 'Bannir et débannir.' },
  broadcast: { label: 'Annonces', description: 'Diffuser un message à tous les joueurs.' },
  world: { label: 'Monde et placement', description: 'Placer ou retirer des stations, boîtes aux lettres, objets, et masquer des objets de la carte.' },
  restart: { label: 'Redémarrage', description: 'Lancer ou annuler un compte à rebours de redémarrage.' },
  weather: { label: 'Météo et heure', description: "Changer la météo et l'heure du jeu." },
  reports: { label: 'Signalements', description: 'Lire et traiter les signalements des joueurs.' },
  housing: { label: 'Logements', description: 'Gérer les logements et leurs propriétaires.' },
  debug: { label: 'Debug', description: 'Outils de diagnostic.' },
  console: { label: 'Commandes en jeu', description: 'Les commandes du chat et de la console réservées au staff (zones, taille, blasons...).' },
  staff: { label: 'Gestion du staff', description: 'Réservée aux propriétaires (fichier de configuration du serveur).' },
};

const adminMock = {
  caps: ALL_ADMIN_CAPS.slice(),
  detail: null as null | Record<string, any>,
  weather: null as null | Record<string, any>,
  big: false,
  installed: false,
};

const buildAdminDetail = (caps: string[], big = false) => {
  const names = [
    'Aela Ravenne', 'Brynjolf Aldric', 'Cassia Vorn', 'Dagny Blackwater', 'Eirik Stonehand', 'Freydis Halan', 'Gunnar Ostrel',
    'Hilde Marrow', 'Ivar Dunmoor', 'Jorunn Skaal', 'Knut Valdis', 'Lydia Ferren', 'Maren Voss', 'Njal Torvik', 'Ysolde Karth',
    'Orsik Brun', 'Petra Lindqvist', 'Ragnar Eldmoor', 'Sigrid Hoarfrost', 'Tormund Halk', 'Ulfric Dray', 'Vilkas Neth',
    'Wren Ashdown', 'Xander Mol', 'Yrsa Vaan', 'Zephyr Quill', 'Alvor Thane', 'Belethor Gris', 'Camilla Vale', 'Delphine Roux',
    'Esbern Kal', 'Farkas Dorn', 'Gerdur Lin', 'Hadvar Ysk', 'Idgrod Faye', 'Jarl Mond', 'Kodlak Ire', 'Lucan Ves', 'Mjoll Aud', 'Nazeem Tul',
  ];
  const profs = ['Mineur (Apprenti)', 'Archer (Novice, spé)', 'Forgeron (Adepte)', 'Alchimiste (Novice)', '', 'Bûcheron (Apprenti)', 'Marchand (Expert)', ''];
  const players = names.map((n, i) => {
    const online = i < 14;
    return {
      a: (0xff000800 + i).toString(16),
      p: 100 + i,
      n,
      d: i % 7 === 3 ? '' : String(284500000000000000 + i * 7919),
      ip: online ? `192.168.${i}.***` : '',
      hwid: online ? `HW-${(0xabc000 + i * 977).toString(16).toUpperCase()}-${(i * 31337).toString(16).toUpperCase()}` : '',
      online,
      ping: online ? 18 + ((i * 13) % 90) : null,
      prof: profs[i % profs.length],
      slot2: i % 5 === 0,
      banned: i === 33 || i === 38,
    };
  });
  if (big) {
    for (let i = 0; i < 5000; i++) {
      const online = i % 9 === 0;
      players.push({ a: (0xff010000 + i).toString(16), p: 1000 + i, n: `${names[i % names.length].split(' ')[0]} Fictif ${i}`, d: String(285000000000000000 + i), ip: online ? `10.0.${i % 250}.***` : '', hwid: online ? `HW-${i}` : '', online, ping: online ? 20 + (i % 120) : null, prof: profs[i % profs.length], slot2: false, banned: i % 400 === 0 });
    }
  }
  const places = ['Blancherive, place du marché', 'Solitude, palais de Châteaubleu', 'Faillaise, grande forge', 'Vendeaume, canaux', 'Rorikstead', 'Morthal, marais', 'Aubetoit', 'Fort Dawnguard', 'Fortdhiver, collège', 'Rivebois, scierie',
    'Cairn de Bleakwind', 'Tour de Hautebrume', 'Camp de mercenaires', 'Sanctuaire de Meridia', 'Ruines de Fenêtre-du-Nord', 'Pont du Dragon', 'Haut-Hrothgar', 'Lac Ilinalta', 'Mine de Rougemont', 'Route de Cormaq'];
  return {
    players,
    locations: places.map((name) => ({ name })),
    modes: [
      { id: 'god', label: 'Dieu', active: false },
      { id: 'noclip', label: 'Traverser les murs', active: false },
      { id: 'ghost', label: 'Fantôme', active: false },
      { id: 'invis', label: 'Invisible', active: true },
      { id: 'unlimitedhealth', label: 'Santé illimitée', active: false },
      { id: 'unlimitedmagicka', label: 'Magie illimitée', active: false },
      { id: 'unlimitedstamina', label: 'Vigueur illimitée', active: false },
      { id: 'speedboost', label: 'Vitesse augmentée', active: false },
      { id: 'mapedit', label: 'Édition du monde (ALT)', active: false },
    ],
    reports: [
      { id: 'r1', reporterName: 'Maren Voss', text: "Un joueur bloque l'entrée de la forge depuis une heure et refuse de bouger malgré les demandes répétées.", cellOrWorldDesc: 'Faillaise', createdAt: Date.now() - 25 * 60000, status: 'open' },
      { id: 'r2', reporterName: 'Freydis Halan', text: 'Cheval coincé dans le pont de Rorikstead.', cellOrWorldDesc: 'Rorikstead', createdAt: Date.now() - 5 * 3600000, status: 'open' },
      { id: 'r3', reporterName: 'Njal Torvik', text: 'Insultes en vocal.', cellOrWorldDesc: 'Blancherive', createdAt: Date.now() - 30 * 3600000, status: 'resolved', resolvedByName: 'Ysolde Karth', resolvedAt: Date.now() - 28 * 3600000 },
    ],
    houses: [
      { refrId: 1001, displayName: 'Maison du marché', holdId: 'whiterun', holdName: 'Blancherive', ownerName: 'Maren Voss', memberCount: 2 },
      { refrId: 1002, displayName: 'Chaumière du pont', holdId: 'rift', holdName: 'La Faille', ownerName: 'Aela Ravenne', memberCount: 0 },
      { refrId: 1003, displayName: 'Atelier des quais', holdId: 'haafingar', holdName: 'Haafingar', ownerName: null, memberCount: 1 },
    ],
    me: { caps },
  };
};

const buildAdminWeather = () => ({
  current: { formId: 0x10, editorId: 'SkyrimClear', forced: false },
  list: [
    { editorId: 'SkyrimClear', formId: '0x10', category: 'Ciel dégagé' },
    { editorId: 'SkyrimClearSN', formId: '0x11', category: 'Ciel dégagé' },
    { editorId: 'SkyrimCloudy', formId: '0x12', category: 'Nuageux' },
    { editorId: 'SkyrimOvercast', formId: '0x13', category: 'Nuageux' },
    { editorId: 'SkyrimFog', formId: '0x14', category: 'Brume' },
    { editorId: 'SkyrimRain', formId: '0x15', category: 'Pluie' },
    { editorId: 'SkyrimStorm', formId: '0x16', category: 'Pluie' },
    { editorId: 'SkyrimSnow', formId: '0x17', category: 'Neige' },
    { editorId: 'SkyrimBlizzard', formId: '0x18', category: 'Neige' },
  ],
  time: { hour: 14, minute: 32, offsetMinutes: 0, adjusted: false },
});


// ---- staff register mock (contract: roles, members with overrides, journal) ----

const ADMIN_NOW = Date.now();
const ADMIN_DAY = 86400000;

const adminStaff = {
  roles: [
    { id: 'moderator', name: 'Modérateur', description: 'Joueurs, expulsions, bannissements, signalements, annonces.', color: '#7aa2b8', caps: ['players', 'kick', 'ban', 'broadcast', 'reports'], builtin: true },
    { id: 'animator', name: 'Animateur', description: 'Joueurs, téléportation, modes, objets, météo, annonces.', color: '#8fbf8a', caps: ['players', 'teleport', 'modes', 'items', 'broadcast', 'weather'], builtin: true },
    { id: 'builder', name: 'Bâtisseur', description: 'Monde et placement, objets, téléportation, modes.', color: '#c9a24f', caps: ['teleport', 'modes', 'items', 'world'], builtin: true },
    { id: 'readonly', name: 'Lecture seule', description: 'Aucun menu, hormis le statut du serveur.', color: '#b9b3a4', caps: [] as string[], builtin: true },
  ] as Array<Record<string, any>>,
  members: [
    { discordId: '284100000000000010', name: 'Petra Lindqvist', roleId: 'moderator', caps: ['weather'], denied: ['ban'], note: 'Modératrice du soir', active: true, expiresAt: null, addedBy: 'Ysolde Karth', addedAt: ADMIN_NOW - 12 * ADMIN_DAY, updatedAt: ADMIN_NOW - 2 * ADMIN_DAY, lastSeenAt: ADMIN_NOW - 40 * 60000, characters: ['Petra Lindqvist'], online: true },
    { discordId: '284100000000000011', name: 'Ragnar Eldmoor', roleId: 'animator', caps: [], denied: [], note: 'Animation des événements', active: true, expiresAt: ADMIN_NOW + 5 * ADMIN_DAY, addedBy: 'Ysolde Karth', addedAt: ADMIN_NOW - 40 * ADMIN_DAY, updatedAt: ADMIN_NOW - 40 * ADMIN_DAY, lastSeenAt: ADMIN_NOW - 3 * 3600000, characters: ['Ragnar Eldmoor', 'Tormund Halk'], online: false },
    { discordId: '284100000000000012', name: 'Wren Ashdown', roleId: 'builder', caps: ['housing'], denied: [], note: 'En test', active: false, expiresAt: null, addedBy: 'Ysolde Karth', addedAt: ADMIN_NOW - ADMIN_DAY, updatedAt: ADMIN_NOW - ADMIN_DAY, lastSeenAt: 0, characters: [], online: false },
    { discordId: '284100000000000013', name: 'Sigrid Hoarfrost', roleId: 'moderator', caps: [], denied: [], note: '', active: true, expiresAt: ADMIN_NOW - 2 * ADMIN_DAY, addedBy: 'Ysolde Karth', addedAt: ADMIN_NOW - 60 * ADMIN_DAY, updatedAt: ADMIN_NOW - 30 * ADMIN_DAY, lastSeenAt: ADMIN_NOW - 4 * ADMIN_DAY, characters: ['Sigrid Hoarfrost'], online: false },
    { discordId: '284100000000000014', name: 'Jorunn Skaal', roleId: null, caps: ['reports', 'players'], denied: [], note: 'Permissions à la carte', active: true, expiresAt: null, addedBy: 'Ysolde Karth', addedAt: ADMIN_NOW - 8 * ADMIN_DAY, updatedAt: ADMIN_NOW - 8 * ADMIN_DAY, lastSeenAt: ADMIN_NOW - 26 * 3600000, characters: ['Jorunn Skaal'], online: false },
  ] as Array<Record<string, any>>,
  audit: [
    { at: ADMIN_NOW - 2 * ADMIN_DAY, by: 'Ysolde Karth', action: 'member.update', target: 'Petra Lindqvist', detail: 'Permission retirée : Bannissements. Permission ajoutée : Météo et heure.' },
    { at: ADMIN_NOW - 12 * ADMIN_DAY, by: 'Ysolde Karth', action: 'member.add', target: 'Petra Lindqvist', detail: 'Ajoutée avec le rôle Modérateur.' },
    { at: ADMIN_NOW - 30 * ADMIN_DAY, by: 'Ysolde Karth', action: 'member.expire', target: 'Sigrid Hoarfrost', detail: "L'accès temporaire est arrivé à échéance." },
    { at: ADMIN_NOW - 40 * ADMIN_DAY, by: 'Ysolde Karth', action: 'role.update', target: 'Animateur', detail: 'Permission ajoutée : Météo et heure.' },
  ] as Array<Record<string, any>>,
};

const adminCapOrder = ALL_ADMIN_CAPS.filter((c) => c !== 'staff');

const staffMemberOut = (m: Record<string, any>) => {
  const role = adminStaff.roles.find((r) => r.id === m.roleId);
  const expired = !!m.expiresAt && m.expiresAt < Date.now();
  const live = m.active && !expired;
  const eff = adminCapOrder.filter((c) => ((role && role.caps.indexOf(c) !== -1) || m.caps.indexOf(c) !== -1) && m.denied.indexOf(c) === -1);
  return { ...m, expired, effectiveCaps: live ? eff : [] };
};

const adminStaffPacket = () => ({
  canManage: true,
  owners: [
    { discordId: '284100000000000001', characters: ['Ysolde Karth'], online: true },
    { discordId: '284100000000000002', characters: [], online: false },
  ],
  members: adminStaff.members.map(staffMemberOut),
  roles: adminStaff.roles.map((r) => ({ ...r })),
  capInfo: ALL_ADMIN_CAPS.map((id) => ({ id, ...ADMIN_CAP_INFO[id] })),
  audit: adminStaff.audit.slice(0, 50),
});

const adminAuditPush = (action: string, target: string, detail: string) => {
  adminStaff.audit.unshift({ at: Date.now(), by: 'Ysolde Karth', action, target, detail });
};

const handleStaffAction = (action: string, p: Record<string, any>, reply: (ok: boolean, text: string) => void): boolean => {
  const refresh = () => dispatch('skymp5-client:adminStaff', adminStaffPacket());
  if (action === 'adminStaffRequest') {
    refresh();
  } else if (action === 'adminStaffCandidates') {
    const q = String(p.query || '').trim().toLowerCase();
    const results = (adminMock.detail ? adminMock.detail.players : [])
      .filter((x: any) => !q || x.n.toLowerCase().indexOf(q) !== -1)
      .slice(0, 12)
      .map((x: any) => ({ name: x.n, discordId: x.d || null, online: x.online, profileId: x.p }));
    dispatch('skymp5-client:adminStaffCandidates', { query: q.slice(0, 48), results });
  } else if (action === 'adminStaffUpsert') {
    const idx = adminStaff.members.findIndex((m) => m.discordId === p.discordId);
    const base = idx >= 0 ? adminStaff.members[idx] : { discordId: p.discordId, name: '', roleId: null, caps: [], denied: [], note: '', active: true, expiresAt: null, addedBy: 'Ysolde Karth', addedAt: Date.now(), lastSeenAt: 0, characters: [], online: false };
    const next = { ...base, updatedAt: Date.now() } as Record<string, any>;
    ['name', 'roleId', 'caps', 'denied', 'note', 'active', 'expiresAt'].forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(p, k)) next[k] = p[k];
    });
    if (idx >= 0) adminStaff.members[idx] = next;
    else adminStaff.members.push(next);
    const kind = idx < 0 ? 'member.add' : base.active && !next.active ? 'member.suspend' : !base.active && next.active ? 'member.activate' : 'member.update';
    adminAuditPush(kind, next.name || next.discordId, idx < 0 ? 'Ajouté au staff.' : 'Permissions ou réglages modifiés.');
    reply(true, `${next.name || next.discordId} : enregistré.`);
    refresh();
  } else if (action === 'adminStaffRemove') {
    const m = adminStaff.members.find((x) => x.discordId === p.discordId);
    adminStaff.members = adminStaff.members.filter((x) => x.discordId !== p.discordId);
    adminAuditPush('member.remove', m ? m.name || m.discordId : String(p.discordId), 'Retiré du staff.');
    reply(true, "Ce membre n'est plus du staff.");
    refresh();
  } else if (action === 'adminStaffRoleUpsert') {
    if (p.id) {
      const r = adminStaff.roles.find((x) => x.id === p.id);
      if (r) Object.assign(r, { name: p.name, description: p.description, color: p.color, caps: p.caps });
      adminAuditPush('role.update', String(p.name), 'Rôle modifié.');
    } else {
      adminStaff.roles.push({ id: 'role-' + (adminStaff.roles.length + 1), name: p.name, description: p.description, color: p.color, caps: p.caps, builtin: false });
      adminAuditPush('role.add', String(p.name), 'Rôle créé.');
    }
    reply(true, `Rôle ${p.name} enregistré.`);
    refresh();
  } else if (action === 'adminStaffRoleRemove') {
    const used = adminStaff.members.some((m) => m.roleId === p.id);
    const r = adminStaff.roles.find((x) => x.id === p.id);
    if (!r || r.builtin) reply(false, 'Un rôle intégré ne se supprime pas.');
    else if (used) reply(false, 'Ce rôle est encore utilisé.');
    else {
      adminStaff.roles = adminStaff.roles.filter((x) => x.id !== p.id);
      adminAuditPush('role.remove', r.name, 'Rôle supprimé.');
      reply(true, `Rôle ${r.name} supprimé.`);
    }
    refresh();
  } else if (action === 'adminStaffBulk') {
    const ids: string[] = p.discordIds || [];
    if (p.action === 'remove') adminStaff.members = adminStaff.members.filter((m) => ids.indexOf(m.discordId) === -1);
    adminStaff.members.forEach((m) => {
      if (ids.indexOf(m.discordId) === -1) return;
      if (p.action === 'suspend') m.active = false;
      if (p.action === 'activate') m.active = true;
      if (p.action === 'setRole') {
        m.roleId = p.roleId;
        m.caps = [];
        m.denied = [];
      }
      if (p.action === 'extend') m.expiresAt = Math.max(Date.now(), m.expiresAt || 0) + (p.days || 0) * ADMIN_DAY;
    });
    adminAuditPush('bulk', `${ids.length} membre(s)`, `Action groupée : ${p.action}.`);
    reply(true, `Action groupée appliquée à ${ids.length} membre(s).`);
    refresh();
  } else if (action === 'adminStaffImport') {
    (p.roles || []).forEach((r: any) => {
      const ex = r.id ? adminStaff.roles.find((x) => x.id === r.id) : undefined;
      if (ex) Object.assign(ex, r);
      else adminStaff.roles.push({ ...r, id: r.id || 'role-' + (adminStaff.roles.length + 1), builtin: false });
    });
    (p.members || []).forEach((m: any) => {
      const i = adminStaff.members.findIndex((x) => x.discordId === m.discordId);
      if (i >= 0) Object.assign(adminStaff.members[i], m, { updatedAt: Date.now() });
      else adminStaff.members.push({ addedBy: 'Ysolde Karth', addedAt: Date.now(), updatedAt: Date.now(), lastSeenAt: 0, characters: [], online: false, ...m });
    });
    adminAuditPush('bulk', 'Import', `${(p.roles || []).length} rôle(s) et ${(p.members || []).length} membre(s) fusionnés.`);
    reply(true, 'Import terminé.');
    refresh();
  } else if (action === 'adminStaffAuditRequest') {
    const m = p.discordId ? adminStaff.members.find((x) => x.discordId === p.discordId) : null;
    const entries = adminStaff.audit.filter((e) => !p.discordId || (m && (e.target === m.name || e.target === m.discordId)));
    dispatch('skymp5-client:adminStaffAudit', { discordId: p.discordId || null, entries });
  } else {
    return false;
  }
  return true;
};

const showAdminPanel = (caps: string[], big = false) => {
  adminMock.caps = caps;
  if (adminMock.big !== big) adminMock.detail = null;
  adminMock.big = big;
  adminMock.detail = adminMock.detail || buildAdminDetail(caps, big);
  adminMock.detail.me = { caps };
  adminMock.weather = adminMock.weather || buildAdminWeather();
  dispatch('skymp5-client:showAdminMenu', adminMock.detail);
};

const installAdminMockServer = () => {
  if (adminMock.installed) return;
  adminMock.installed = true;
  const reply = (ok: boolean, text: string) => dispatch('skymp5-client:adminActionResult', { ok, text });
  window.addEventListener('skymp5-front:outgoing', (e: Event) => {
    const p = (e as CustomEvent).detail as Record<string, any>;
    const action = String(p.action || '');
    window.setTimeout(() => {
      if (action === 'close') dispatch('skymp5-client:hideAdminMenu');
      else if (action === 'refresh') showAdminPanel(adminMock.caps, adminMock.big);
      else if (action === 'requestServerStatus') {
        dispatch('skymp5-client:adminServerStatus', { tps: 19.8, onlinePlayers: 14, uptimeSec: 3 * 86400 + 4 * 3600 + 600 });
      } else if (action === 'weatherList' || action === 'weatherSet' || action === 'weatherClear' || action === 'timeSet' || action === 'timeShift' || action === 'timeReset') {
        if (action === 'weatherSet' && adminMock.weather) {
          const w = adminMock.weather.list.find((x: any) => x.formId.replace(/^0x/i, '') === p.target);
          if (w) adminMock.weather.current = { formId: parseInt(w.formId, 16), editorId: w.editorId, forced: true };
        }
        if (action === 'weatherClear' && adminMock.weather) adminMock.weather.current.forced = false;
        dispatch('skymp5-client:adminWeather', adminMock.weather);
      } else if (action === 'toggleMode' && adminMock.detail) {
        const m = adminMock.detail.modes.find((x: any) => x.id === p.mode);
        if (m) m.active = !m.active;
        showAdminPanel(adminMock.caps, adminMock.big);
      } else if (handleStaffAction(action, p, reply)) {
        // answered above
      } else if (action === 'debugTabOpen') {
        dispatch('skymp5-client:updateAdminDebug', { name: 'Porte de Blancherive', refId: 'a1b2c', refDesc: null, baseId: '2e6a1', baseDesc: 'Door', cell: '3c', cellName: 'Blancherive', pos: [1200, -340, 80], dist: 214, live: true });
      } else if (action === 'broadcastMessage') {
        reply(true, 'Message envoyé à 14 joueur(s)');
      } else if (['heal', 'revive', 'freeze', 'teleportTo', 'summon', 'kick', 'ban', 'unban', 'giveGold', 'wipeInventory', 'teleportLoc', 'toggleModeForTarget', 'reviveZone', 'grantExtraSlot', 'revokeExtraSlot', 'openRaceMenu', 'openItemGiverFor'].indexOf(action) !== -1) {
        reply(true, `Action ${action} simulée (aperçu sans serveur)`);
      }
    }, 120);
  });
};

export function mountDevPreviewPanel(): void {
  installAdminMockServer();
  const container = document.createElement('div');
  container.style.cssText = [
    'position:fixed', 'top:0', 'left:0', 'width:220px', 'max-height:100vh',
    'overflow-y:auto', 'background:rgba(10,10,10,0.9)', 'z-index:999999',
    'font-family:sans-serif', 'font-size:12px', 'padding:6px',
  ].join(';');

  const title = document.createElement('div');
  title.textContent = 'Dev preview panels';
  title.style.cssText = 'color:#fff;font-weight:bold;margin-bottom:6px;';
  container.appendChild(title);

  for (const panel of panels) {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:4px;margin-bottom:2px;';

    const showBtn = document.createElement('button');
    showBtn.textContent = panel.label;
    showBtn.style.cssText = 'flex:1;text-align:left;cursor:pointer;';
    showBtn.onclick = panel.show;
    row.appendChild(showBtn);

    if (panel.hide) {
      const hideBtn = document.createElement('button');
      hideBtn.textContent = 'x';
      hideBtn.style.cssText = 'cursor:pointer;';
      hideBtn.onclick = panel.hide;
      row.appendChild(hideBtn);
    }

    container.appendChild(row);
  }

  document.body.appendChild(container);
}
